import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Hand } from 'lucide-react';
import { PRESET_PREFIX, SALUTO_PRESET_KEY, eMessaggioMio } from '@draughts/shared';
import type { BoardState, ChatMessage } from '@draughts/shared';
import { useChatStore } from '../../store/chat-store';
import { haMosso, mosseGiocate } from '../../utils/ha-mosso';
import { SALUTO_CONFERMA_MS, SALUTO_INVITO_MS, SALUTO_RICEVUTO_MS } from '../../constants/timing';
import '../shared/offer-bar.css';
import './SalutoBuonGioco.css';

/**
 * IL SALUTO D'APERTURA, senza aprire la chat.
 *
 * Prima di giocare la propria prima mossa la barra di gioco diventa «Buon gioco!»:
 * un tocco e l'augurio parte. All'avversario la barra dice «Mario: Buon gioco!» con
 * un bottone «Anche a te» che glielo rimanda con un altro tocco solo. La chat non
 * si apre mai da nessuna delle due parti.
 *
 * PERCHE' DENTRO LA BARRA e non sopra. E' gia' cosi' che questa app dice «il tuo
 * avversario ti sta parlando»: l'offerta di patta e il rifai-mossa DIVENTANO la
 * barra (vedi i due early-return di `GameToolbar`), e l'esito della rivincita
 * indossa lo stesso vestito. La prima versione galleggiava sopra i comandi come
 * pastiglia, ed era una quarta grammatica che si portava dietro i suoi guai —
 * `pointer-events` da negoziare con la damiera, il cartellino del giocatore coperto
 * da 480px in su, un gradino CSS per il conteggio patta, una media query per il
 * desktop. Tutto questo sparisce insieme alla pastiglia.
 *
 * Sul filo NON c'e' niente di nuovo: e' un `game:chat` con `preset:goodLuck`, che
 * il server valida, tagga e consegna — e che ogni lettore vede nella propria
 * lingua. Qui cambia solo la presentazione.
 */

/**
 * Il nome del mittente, accorciato.
 *
 * L'ellissi la farebbe anche il CSS, ma su tutta la riga: con un nome lunghissimo
 * a 360px quello che finiva tagliato era l'AUGURIO («GiocatoreLunghissimo123456:
 * Bu…»), cioe' l'unica cosa che la barra ha da dire. Tagliando qui il taglio cade
 * dove deve — e il nome per intero resta due dita piu' su, sul cartellino, che lo
 * accorcia allo stesso modo.
 */
export function abbreviaNome(nome: string, max = 12): string {
  return nome.length > max ? `${nome.slice(0, max - 1)}…` : nome;
}

/*
 * Il ripiego per uno store PARZIALE.
 *
 * Meta' delle schermate di gioco, nei test, montano un chat-store finto che ha
 * solo i campi che servivano a loro: qui arrivano `undefined` sia il saluto sia le
 * azioni, e una chiamata nuda faceva cadere la schermata intera (misurato: sei
 * test di resa e patta in arena e torneo, tutti verdi fino al giorno prima).
 * Identita' stabile perche' un `() => {}` creato a ogni render rientrerebbe nelle
 * dipendenze degli effetti e li rilancerebbe in continuazione.
 */
const NIENTE = () => {};
/* Stesso ripiego per il transcript, e la stessa ragione per cui e' una costante di
   modulo: un `[]` nuovo a ogni render e' un riferimento nuovo a ogni render. */
const NESSUN_MESSAGGIO: ChatMessage[] = [];

export interface StatoSaluto {
  /** Quale delle tre facce: l'invito, il saluto ricevuto, la conferma d'invio. */
  tipo: 'invita' | 'ricevuto' | 'inviato';
  /**
   * Chi ha salutato (solo `ricevuto`): gia' accorciato e gia' con la punteggiatura
   * della lingua — il francese vuole lo spazio prima dei due punti («Nom :»), ed e'
   * per questo che passa da una chiave invece che da una concatenazione in JSX.
   */
  mittente?: string;
  /** La frase, nella lingua di chi legge. */
  frase: string;
  /**
   * Il bottone «Anche a te». Sparisce se ho gia' salutato io: a saluto ricambiato
   * non c'e' piu' niente da rimandare, e un secondo invio sarebbe l'unico modo di
   * sfiorare il limite di un messaggio ogni secondo e mezzo.
   */
  mostraRisposta: boolean;
  onInvia: () => void;
}

interface OpzioniSaluto {
  /** La damiera, da cui si deriva «ho gia' mosso». `unknown` come in GameScreen. */
  boardState: unknown;
  playerColor: 'white' | 'black';
  /** Le semi-mosse dell'apertura sorteggiata: non sono mosse di nessuno. */
  openingPlyCount?: number;
  /**
   * STRUTTURALE: in questa modalita' il saluto non esiste affatto — partita finita,
   * spettatore, partita locale o in presenza. Cintura sopra le bretelle del
   * contesto chat, cosi' una modalita' nuova non erediti il saluto per distrazione.
   */
  abilitato: boolean;
  /**
   * TRANSITORIO: qualcun altro sta parlando nello stesso punto (una proposta di
   * patta, un rifai-mossa, l'esito di una patta rifiutata, la scheda «avversario
   * trovato»). Il saluto cede sempre il passo: una cortesia non deve coprire una
   * negoziazione, ne' consumare i suoi secondi dietro una scheda.
   */
  sospeso: boolean;
}

/**
 * Che cosa deve dire la barra, o `null` se deve restare quella dei comandi.
 *
 * Sta in un hook, e non dentro un componente che decide da se', perche' la
 * risposta non serve solo a lui: chi monta la barra deve sapere se rendere il
 * saluto O `GameToolbar`. E' la stessa ragione — e lo stesso stampo — di
 * `useCanBoost` in ArenaBoostButton.
 *
 * UN SALUTO PER STANZA, non per partita, ed e' il transcript a dirlo.
 *
 * Qui c'era scritto il contrario — stati locali soltanto, «tanto la rivincita
 * rimonta GameScreen» — e quella premessa era falsa due volte. Falsa in fatto:
 * `App` resta su `screen === 'online-game'` e `OnlineGame` rende `GameScreen`
 * senza `key`, quindi nel percorso normale non si rimonta niente e gli stati
 * sopravvivono alle manche. E falsa in intenzione: anche rimontando, la
 * rivincita e il ritorno di un andata-e-ritorno riusano la stessa stanza e la
 * stessa conversazione, dove un secondo «Buon gioco!» a pochi secondi dal primo
 * non lo manda nessuno (e il bot ora tace, vedi `_salutaApertura`).
 *
 * Quindi «ho gia' salutato qui» si legge dal transcript, che e' l'unico posto
 * dove il fatto e' davvero registrato: sopravvive alle manche perche' il roomId
 * non cambia, torna dopo un ricarico con `restoreMessages`, e si azzera da solo
 * quando la stanza cambia (l'arena ne apre una per partita). Sparisce anche il
 * prezzo che questo commento dichiarava: un ricarico prima della propria prima
 * mossa non rimette piu' l'invito a chi aveva gia' salutato.
 *
 * Due limiti, dichiarati e non inseguiti: senza `isOwn` (riga senza identita')
 * `eMessaggioMio` ripiega sul colore, che fra le due manche e' invertito — lo
 * stesso compromesso che le bolle della chat gia' accettano; e il transcript
 * tiene gli ultimi `MAX_MESSAGES_PER_ROOM` messaggi, per cui una conversazione
 * lunghissima spinge fuori il saluto e l'invito torna.
 *
 * Il resto degli stati resta locale: sono cose della SCHERMATA — l'invito gia'
 * scaduto, la barra gia' ceduta a una patta — non fatti della conversazione.
 */
export function useSaluto({
  boardState,
  playerColor,
  openingPlyCount = 0,
  abilitato,
  sospeso,
}: OpzioniSaluto): StatoSaluto | null {
  const { t } = useTranslation();
  const frase = t(`chat.presets.${SALUTO_PRESET_KEY}`);

  // Selettori e non `getState()`: nei test di componente gli store sono finti
  // selettori senza `.getState` (vedi la nota in OnlineGame.tsx).
  const roomId = useChatStore((s) => s.roomId);
  const canChat = useChatStore((s) => s.canChat);
  const opponentLeft = useChatStore((s) => s.opponentLeft);
  const chatAperta = useChatStore((s) => s.isOpen);
  /* `?? null` per la stessa ragione del ripiego qui sopra: con uno store parziale
     arriva `undefined`, e una guardia `!== null` lo lascerebbe passare fino alla
     lettura del nome del mittente. */
  const salutoRicevuto = useChatStore((s) => s.salutoRicevuto) ?? null;
  const messaggi = useChatStore((s) => s.messages) ?? NESSUN_MESSAGGIO;
  const send = useChatStore((s) => s.send) ?? NIENTE;
  const salutoLetto = useChatStore((s) => s.salutoLetto) ?? NIENTE;
  const chiudiSaluto = useChatStore((s) => s.chiudiSaluto) ?? NIENTE;

  const [inviato, setInviato] = useState(false);
  const [conferma, setConferma] = useState(false);
  /* L'invito ha i suoi secondi e poi SI RITIRA. Nasce da quando toglieva il posto
     ai comandi — tenerli via per tutta l'attesa della prima mossa, che a cadenza
     lunga sono minuti, era inaccettabile. Oggi i comandi non li tocca piu', ma la
     scadenza resta e per una ragione sua: da mobile il posto lo prende alle frecce
     di revisione, a «Mosse» e alla Chat, e un invito non raccolto non puo' tenersele
     per sempre. */
  const [invitoScaduto, setInvitoScaduto] = useState(false);
  /* Una volta ceduto il passo, l'invito non torna: ricomparire dopo il rifiuto di
     una patta sarebbe un augurio fuori tempo. Il saluto RICEVUTO invece e' il
     messaggio di una persona, e riappare appena il campo e' libero. */
  const [ceduto, setCeduto] = useState(false);
  /*
   * L'id del saluto che ha gia' avuto la barra. Fa due lavori con un campo solo:
   * il badge dei non letti si ritira una volta sola, e — soprattutto — UN SALUTO
   * PER PARTITA. Senza, bastava ripetere «Buon gioco!» dalla tavolozza della chat
   * per far ricomparire la barra all'avversario quante volte si voleva, anche a
   * meta' partita: una cortesia che si ripete e' disturbo. I saluti successivi
   * restano righe di chat, e alzano il badge come qualunque messaggio.
   */
  const [idMostrato, setIdMostrato] = useState<string | null>(null);
  /* `useRef` e non stato: due tocchi nello stesso frame vedrebbero lo stesso
     `inviato: false` e spedirebbero due volte. */
  const inviatoRef = useRef(false);

  const contestoChat = roomId !== null && canChat && !opponentLeft;
  /*
   * Ho gia' salutato in QUESTA stanza — l'andata, o la partita prima della
   * rivincita. Per IDENTITA' e non per colore: i colori si scambiano a ogni
   * manche, quindi un augurio mandato da bianco all'andata e' ancora mio mentre
   * gioco nero al ritorno. `eMessaggioMio` e' lo stesso predicato con cui la chat
   * allinea le bolle e conta i non letti.
   */
  const hoGiaSalutatoQui = messaggi.some(
    (m) => m.presetKey === SALUTO_PRESET_KEY && eMessaggioMio(m, playerColor)
  );
  const board = boardState && typeof boardState === 'object' ? (boardState as Partial<BoardState>) : null;
  const turnoCorrente = board?.currentTurn ?? null;
  const mosse = mosseGiocate(board?.moveHistory?.length ?? 0, openingPlyCount);
  const hoGiaMosso = haMosso(playerColor, mosse, turnoCorrente);

  const invia = useCallback(() => {
    if (inviatoRef.current) return;
    // Se il limite a raffica lo trattiene, «inviato» sarebbe falso: si resta
    // come prima e il saluto si puo' ritoccare. `=== false` e non `!`: con lo
    // store parziale il ripiego `NIENTE` restituisce `undefined`.
    if (send(`${PRESET_PREFIX}${SALUTO_PRESET_KEY}`) === false) return;
    inviatoRef.current = true;
    setInviato(true);
    setConferma(true);
  }, [send]);

  /* Rispondere chiude: la barra ha finito il suo lavoro, e lasciarla a schermo per
     i secondi che le restano vorrebbe dire far aspettare un gesto gia' compiuto.
     Resta la conferma dell'invio, che e' il riscontro. */
  const rispondi = useCallback(() => {
    invia();
    chiudiSaluto();
  }, [invia, chiudiSaluto]);

  useEffect(() => {
    if (sospeso) setCeduto(true);
  }, [sospeso]);

  /** Il primo saluto della partita, o quello che sta gia' parlando. */
  const primoSaluto = idMostrato === null || idMostrato === salutoRicevuto?.id;
  const mostraRicevuto = abilitato && !sospeso && salutoRicevuto !== null && !chatAperta && primoSaluto;

  /* La barra E' la lettura: il badge dei non letti si ritira, una volta per
     messaggio. Un saluto che NON ottiene la barra — la chat e' a vista, o ha gia'
     parlato il suo — si scarta dallo store senza toccare il badge: resta una riga
     di chat non letta come tutte le altre. */
  useEffect(() => {
    if (salutoRicevuto === null) return;
    if (mostraRicevuto) {
      if (idMostrato !== salutoRicevuto.id) {
        setIdMostrato(salutoRicevuto.id);
        salutoLetto();
      }
      return;
    }
    if (chatAperta || !primoSaluto) chiudiSaluto();
  }, [mostraRicevuto, salutoRicevuto, chatAperta, primoSaluto, idMostrato, salutoLetto, chiudiSaluto]);

  /* Il tempo parte quando la barra si VEDE, non quando il messaggio arriva:
     altrimenti si consumerebbe dietro la scheda «avversario trovato» o dietro una
     proposta di patta, e chi torna a guardare non troverebbe niente. */
  useEffect(() => {
    if (!mostraRicevuto || salutoRicevuto === null) return;
    const id = window.setTimeout(chiudiSaluto, SALUTO_RICEVUTO_MS);
    return () => window.clearTimeout(id);
  }, [mostraRicevuto, salutoRicevuto, chiudiSaluto]);

  useEffect(() => {
    if (!conferma) return;
    const id = window.setTimeout(() => setConferma(false), SALUTO_CONFERMA_MS);
    return () => window.clearTimeout(id);
  }, [conferma]);

  /*
   * Allo smontaggio il saluto pendente si scarta.
   *
   * L'ancora della barra sparisce a fine partita, e la rivincita riusa la STESSA
   * room lato server — quindi lo store non azzera niente. Senza questa riga, un
   * «Buon gioco!» arrivato a partita finita (il momento in cui si propone la
   * rivincita, cioe' proprio quando quella frase si manda) sarebbe rimasto in canna
   * e sarebbe comparso all'inizio della partita dopo, con minuti di ritardo.
   *
   * Via ref perche' il cleanup deve girare SOLO allo smontaggio: legarlo a
   * `chiudiSaluto` lo farebbe scattare a ogni cambio di identita' dell'azione.
   */
  const chiudiRef = useRef(chiudiSaluto);
  chiudiRef.current = chiudiSaluto;
  useEffect(() => () => { chiudiRef.current(); }, []);

  const mostraInvito =
    abilitato && !sospeso && contestoChat && !hoGiaMosso && !inviato && !ceduto && !invitoScaduto
    && !hoGiaSalutatoQui;

  useEffect(() => {
    if (!mostraInvito) return;
    const id = window.setTimeout(() => setInvitoScaduto(true), SALUTO_INVITO_MS);
    return () => window.clearTimeout(id);
  }, [mostraInvito]);

  if (mostraRicevuto && salutoRicevuto !== null) {
    return {
      tipo: 'ricevuto',
      mittente: t('game.saluto.mittente', { username: abbreviaNome(salutoRicevuto.senderUsername) }),
      frase,
      mostraRisposta: !inviato && contestoChat,
      onInvia: rispondi,
    };
  }

  if (!abilitato || sospeso) return null;

  if (conferma) return { tipo: 'inviato', frase, mostraRisposta: false, onInvia: NIENTE };

  if (!mostraInvito) return null;
  return { tipo: 'invita', frase, mostraRisposta: false, onInvia: invia };
}

/**
 * La barra del saluto: presentazionale, senza timer e senza store — come
 * `OfferNotice` e come le due barre d'offerta dentro `GameToolbar`.
 *
 * Indossa il vestito che c'e' gia' (`.game-toolbar` + `.offer-bar`): stessa
 * altezza, stessa cornice, stesso comportamento a 480px, dove il messaggio prende
 * la prima riga e i bottoni la seconda. Il vestito e' quello dei comandi, il posto
 * no: vedi `salutoBarra` in GameScreen.
 */
export default function BarraSaluto({ tipo, mittente, frase, mostraRisposta, onInvia }: StatoSaluto) {
  const { t } = useTranslation();

  if (tipo === 'invita') {
    return (
      <div className="game-toolbar saluto-bar" data-testid="saluto-barra">
        <button
          type="button"
          className="game-toolbar__btn saluto-bar__invita"
          data-testid="saluto-invia"
          onClick={onInvia}
          title={t('game.saluto.invia')}
          aria-label={t('game.saluto.invia')}
        >
          <Hand size={20} aria-hidden="true" />
          <span>{frase}</span>
        </button>
      </div>
    );
  }

  if (tipo === 'inviato') {
    return (
      <div className="game-toolbar saluto-bar" role="status" data-testid="saluto-inviato">
        <span className="offer-bar__text saluto-bar__testo">
          <Check size={16} aria-hidden="true" />
          {t('game.saluto.inviato')}
        </span>
      </div>
    );
  }

  return (
    <div
      className="game-toolbar game-toolbar--draw-offer offer-bar saluto-bar"
      role="status"
      aria-live="polite"
      data-testid="saluto-ricevuto"
    >
      <span className="offer-bar__text saluto-bar__testo">
        <Hand size={16} aria-hidden="true" />
        <span className="saluto-bar__mittente">{mittente}</span>
        <span className="saluto-bar__frase">{frase}</span>
      </span>
      {mostraRisposta && (
        <button
          type="button"
          className="game-toolbar__btn offer-bar__btn--accept"
          data-testid="saluto-anche-a-te"
          onClick={onInvia}
        >
          <Hand size={18} aria-hidden="true" />
          <span>{t('game.saluto.ancheATe')}</span>
        </button>
      )}
    </div>
  );
}
